import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Table,
  Tag,
  Select,
  Space,
  Button,
  Tooltip,
  Modal,
  Tabs
} from "antd";
import {
  AuditOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { searchAuditLogs, getEmployeeAuditLogs } from "../../services/auditService";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const AuditLogsPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    entityType: null,
    action: null
  });
  const [detailModal, setDetailModal] = useState({
    visible: false,
    record: null
  });

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, activeTab, filters]);

  const buildRsqlFilter = () => {
    const conditions = [];

    if (filters.entityType) {
      conditions.push(`entityType==${filters.entityType}`);
    }
    if (filters.action) {
      conditions.push(`action==${filters.action}`);
    }

    return conditions.length > 0 ? conditions.join(";") : null;
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const rsqlFilter = buildRsqlFilter();
      let response;

      if (activeTab === "employees") {
        response = await getEmployeeAuditLogs(
          rsqlFilter,
          pagination.current - 1,
          pagination.pageSize
        );
      } else {
        response = await searchAuditLogs(
          rsqlFilter,
          pagination.current - 1,
          pagination.pageSize
        );
      }

      setLogs(response.data.content || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.totalElements || 0
      }));
    } catch (err) {
      console.error("Error fetching audit logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return "green";
      case "UPDATE":
        return "blue";
      case "DELETE":
        return "red";
      case "STATUS_CHANGE":
        return "orange";
      default:
        return "default";
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "CREATE":
        return "Oluşturma";
      case "UPDATE":
        return "Güncelleme";
      case "DELETE":
        return "Silme";
      case "STATUS_CHANGE":
        return "Durum Değişikliği";
      default:
        return action;
    }
  };

  const getEntityTypeLabel = (entityType) => {
    switch (entityType) {
      case "ORDER":
        return "Sipariş";
      case "ORDER_ITEM":
        return "Sipariş Kalemi";
      case "CUSTOMER":
        return "Müşteri";
      case "EMPLOYEE":
        return "Çalışan";
      case "USER":
        return "Kullanıcı";
      default:
        return entityType;
    }
  };

  const showDetail = (record) => {
    setDetailModal({
      visible: true,
      record
    });
  };

  const columns = [
    {
      title: "Tarih",
      dataIndex: "performedAt",
      key: "performedAt",
      width: 180,
      render: (date) => (
        <Space>
          <ClockCircleOutlined />
          {dayjs(date).format("DD.MM.YYYY HH:mm:ss")}
        </Space>
      ),
      sorter: true
    },
    {
      title: "Varlık",
      dataIndex: "entityType",
      key: "entityType",
      width: 130,
      render: (type, record) => (
        <div>
          <Tag>{getEntityTypeLabel(type)}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            #{record.entityId}
          </Text>
        </div>
      )
    },
    {
      title: "İşlem",
      dataIndex: "action",
      key: "action",
      width: 140,
      render: (action) => (
        <Tag color={getActionColor(action)}>{getActionLabel(action)}</Tag>
      )
    },
    {
      title: "Kullanıcı",
      dataIndex: "performedBy",
      key: "performedBy",
      width: 180,
      render: (user, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{user}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.userType}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: "IP Adresi",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 130,
      render: (ip) => ip || "-"
    },
    {
      title: "Detay",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Tooltip title="Detayları Görüntüle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => showDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  const formatJson = (jsonString) => {
    if (!jsonString) return "-";
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <AuditOutlined style={{ marginRight: 12 }} />
          Denetim Kayıtları
        </Title>
        <Text type="secondary">
          Sistemdeki tüm değişikliklerin kayıtlarını görüntüleyin
        </Text>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "all",
              label: (
                <span>
                  <AuditOutlined />
                  Tüm Kayıtlar
                </span>
              )
            },
            {
              key: "employees",
              label: (
                <span>
                  <TeamOutlined />
                  Çalışan Aktiviteleri
                </span>
              )
            }
          ]}
          style={{ marginBottom: 16 }}
        />

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Varlık Tipi"
            allowClear
            style={{ width: 150 }}
            value={filters.entityType}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, entityType: value }))
            }
          >
            <Option value="ORDER">Sipariş</Option>
            <Option value="ORDER_ITEM">Sipariş Kalemi</Option>
            <Option value="CUSTOMER">Müşteri</Option>
          </Select>
          <Select
            placeholder="İşlem Tipi"
            allowClear
            style={{ width: 150 }}
            value={filters.action}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, action: value }))
            }
          >
            <Option value="CREATE">Oluşturma</Option>
            <Option value="UPDATE">Güncelleme</Option>
            <Option value="DELETE">Silme</Option>
            <Option value="STATUS_CHANGE">Durum Değişikliği</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchLogs}>
            Yenile
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
            pageSizeOptions: ["10", "20", "50", "100"]
          }}
          onChange={handleTableChange}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title="Denetim Kaydı Detayı"
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, record: null })}
        footer={[
          <Button
            key="close"
            onClick={() => setDetailModal({ visible: false, record: null })}
          >
            Kapat
          </Button>
        ]}
        width={700}
      >
        {detailModal.record && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Varlık: </Text>
              <Tag>{getEntityTypeLabel(detailModal.record.entityType)}</Tag>
              <Text> #{detailModal.record.entityId}</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>İşlem: </Text>
              <Tag color={getActionColor(detailModal.record.action)}>
                {getActionLabel(detailModal.record.action)}
              </Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Kullanıcı: </Text>
              <Text>
                {detailModal.record.performedBy} ({detailModal.record.userType})
              </Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Tarih: </Text>
              <Text>
                {dayjs(detailModal.record.performedAt).format(
                  "DD.MM.YYYY HH:mm:ss"
                )}
              </Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>IP Adresi: </Text>
              <Text>{detailModal.record.ipAddress || "-"}</Text>
            </div>

            {detailModal.record.oldValue && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Eski Değer:</Text>
                <pre
                  style={{
                    background: "#f5f5f5",
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 200,
                    overflow: "auto",
                    fontSize: 12
                  }}
                >
                  {formatJson(detailModal.record.oldValue)}
                </pre>
              </div>
            )}

            {detailModal.record.newValue && (
              <div>
                <Text strong>Yeni Değer:</Text>
                <pre
                  style={{
                    background: "#f5f5f5",
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 200,
                    overflow: "auto",
                    fontSize: 12
                  }}
                >
                  {formatJson(detailModal.record.newValue)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogsPage;
